import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { supabase } from '@/lib/supabase';
import { professionCategories } from '@/mocks/professions';
import { businessSectors } from '@/mocks/sectors';
import { AnimImageMagnifier } from '@readdy/anim/card/image-magnifier/react';

const generateSlug = (name: string) => {
  const base = name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return `${base || 'entreprise'}-${Date.now().toString(36)}`;
};

const cities = ['Lomé', 'Sokodé', 'Kara', 'Atakpamé', 'Kpalimé', 'Tsévié', 'Dapaong', 'Autre'];

const RESEND_COOLDOWN_SECONDS = 30;

type AccountType = 'particulier' | 'entreprise' | 'freelance' | 'formateur';

function Register() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [accountType, setAccountType] = useState<AccountType>('particulier');
  const [categories, setCategories] = useState<{ value: string; label: string }[]>([]);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    sector: '',
    businessName: '',
    category: '',
    city: '',
    address: '',
    headline: '',
    skills: '',
    expertise: '',
    termsAccepted: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [status, setStatus] = useState<'idle' | 'loading' | 'error' | 'success'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [verificationPending, setVerificationPending] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [resending, setResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(media.matches);
    const onChange = () => setPrefersReducedMotion(media.matches);
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    let mounted = true;
    const loadCategories = async () => {
      const { data } = await supabase
        .from('categories')
        .select('name')
        .is('parent_id', null)
        .order('name');
      if (mounted && data) {
        setCategories(
          (data as { name: string }[]).map((c) => ({ value: c.name, label: c.name })),
        );
      }
    };
    loadCategories();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const updateField = (field: string, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const isBusiness = accountType === 'entreprise';
  const isFreelance = accountType === 'freelance';
  const isFormateur = accountType === 'formateur';
  const selectedSector = professionCategories.find((c) => c.name === formData.sector);
  const headlineOptions = selectedSector ? selectedSector.professions : [];

  const roleMap: Record<AccountType, string> = {
    particulier: 'user',
    entreprise: 'business',
    freelance: 'freelancer',
    formateur: 'instructor',
  };

  const typeLabels: Record<AccountType, string> = {
    particulier: 'Particulier',
    entreprise: 'Entreprise',
    freelance: 'Freelance',
    formateur: 'Formateur',
  };

  const finalizeRegistration = async () => {
    const { data: sessionData } = await supabase.auth.getUser();
    const userId = sessionData.user?.id;
    if (!userId) {
      setStatus('error');
      setErrorMsg('Impossible de finaliser votre inscription. Veuillez vous reconnecter.');
      return;
    }

    const { error: profileError } = await supabase
      .from('profiles')
      .update({
        full_name: formData.fullName,
        phone: formData.phone,
        sector: formData.sector || null,
        role: roleMap[accountType],
      })
      .eq('id', userId);

    if (profileError) {
      setStatus('error');
      setErrorMsg(profileError.message);
      return;
    }

    if (isBusiness) {
      let categoryId: string | null = null;
      if (formData.category) {
        const { data: catData } = await supabase
          .from('categories')
          .select('id')
          .eq('name', formData.category)
          .maybeSingle();
        categoryId = catData?.id ?? null;
      }

      const { error: businessError } = await supabase.from('businesses').insert({
        owner_id: userId,
        name: formData.businessName,
        slug: generateSlug(formData.businessName),
        category_id: categoryId,
        city: formData.city,
        address: formData.address,
        country: 'Togo',
        status: 'pending',
        verified: false,
      });

      if (businessError) {
        setStatus('error');
        setErrorMsg(businessError.message);
        return;
      }
    }

    if (isFreelance) {
      const { error: freelanceError } = await supabase.from('freelancer_profiles').insert({
        owner_id: userId,
        display_name: formData.fullName,
        headline: formData.headline || null,
        skills: formData.skills || null,
      });

      if (freelanceError) {
        setStatus('error');
        setErrorMsg(freelanceError.message);
        return;
      }
    }

    setStatus('success');
    navigate('/dashboard', { replace: true });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) {
      setStatus('error');
      setErrorMsg('Les mots de passe ne correspondent pas.');
      return;
    }
    setStatus('loading');
    setErrorMsg('');

    const { data, error } = await supabase.auth.signUp({
      email: formData.email,
      password: formData.password,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback`,
        data: { full_name: formData.fullName },
      },
    });

    if (error) {
      setStatus('error');
      const msg = error.message.toLowerCase();
      if (msg.includes('already registered') || msg.includes('already been registered')) {
        setErrorMsg('Un compte existe déjà avec cette adresse email.');
      } else if (msg.includes('password') || msg.includes('weak')) {
        setErrorMsg('Le mot de passe est trop faible. Utilisez au moins 8 caractères.');
      } else {
        setErrorMsg(error.message);
      }
      return;
    }

    if (data.session) {
      await finalizeRegistration();
      return;
    }

    setStatus('idle');
    setVerificationPending(true);
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMsg('');

    const { error } = await supabase.auth.verifyOtp({
      email: formData.email,
      token: verificationCode,
      type: 'signup',
    });

    if (error) {
      setStatus('error');
      setErrorMsg('Code invalide ou expiré. Vérifiez et réessayez.');
      return;
    }

    await finalizeRegistration();
  };

  const handleResend = async () => {
    setResending(true);
    setErrorMsg('');
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email: formData.email,
      options: { emailRedirectTo: `${window.location.origin}/auth/callback` },
    });
    setResending(false);
    if (error) {
      setErrorMsg(error.message);
    } else {
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
      setErrorMsg('Un nouveau code a été envoyé par email.');
    }
  };

  const totalSteps = accountType === 'particulier' ? 2 : 3;

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <Navbar />

      <div className="flex-1 flex items-center justify-center px-4 py-12 md:py-16">
        <div className="w-full max-w-lg">
          {/* Logo */}
          <div className="text-center mb-8">
            {prefersReducedMotion ? (
              <img
                src="https://static.readdy.ai/image/4348d35aef10b02e64c1a80f787cca17/52f74c8d2b3c4f047b22c4e6b94b8b4b.jpeg"
                alt="WorldApeiron"
                className="h-14 w-14 mx-auto mb-4 object-contain"
              />
            ) : (
              <AnimImageMagnifier zoom={2} size={120}>
                <img
                  src="https://static.readdy.ai/image/4348d35aef10b02e64c1a80f787cca17/52f74c8d2b3c4f047b22c4e6b94b8b4b.jpeg"
                  alt="WorldApeiron"
                  className="h-14 w-14 mx-auto mb-4 object-contain"
                />
              </AnimImageMagnifier>
            )}
            <h1 className="font-heading font-bold text-2xl text-foreground-50">
              Créer un compte
            </h1>
            <p className="text-sm text-foreground-400 mt-2">
              Rejoignez l&apos;écosystème WorldApeiron
            </p>
          </div>

          {/* Step indicator */}
          {!verificationPending && status !== 'success' && (
            <div className="flex items-center justify-center gap-2 mb-8">
              {Array.from({ length: totalSteps }).map((_, i) => {
                const s = i + 1;
                return (
                  <div key={s} className="flex items-center gap-2">
                    <div
                      className={`w-8 h-8 flex items-center justify-center rounded-full text-sm font-semibold ${
                        s <= step
                          ? 'bg-primary-500 text-foreground-950'
                          : 'bg-background-200 text-foreground-400'
                      }`}
                    >
                      {s}
                    </div>
                    {s < totalSteps && (
                      <div className={`w-8 h-0.5 ${s < step ? 'bg-primary-500' : 'bg-background-200'}`} />
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {status === 'success' ? (
            <div className="text-center p-6 rounded-xl bg-primary-500/10 border border-primary-400/30">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-primary-500/20">
                <i className="ri-check-line text-3xl text-primary-400" />
              </div>
              <h2 className="font-heading font-bold text-xl text-foreground-50 mb-2">
                Inscription réussie !
              </h2>
              <p className="text-sm text-foreground-400 mb-4">
                {accountType === 'entreprise'
                  ? 'Votre compte et votre entreprise ont été créés. Votre entreprise est en attente de validation.'
                  : accountType === 'freelance'
                    ? 'Votre compte freelance a été créé. Vous pouvez maintenant proposer vos services et compléter votre profil.'
                    : accountType === 'formateur'
                      ? 'Votre compte formateur a été créé. Vous pouvez maintenant publier vos formations.'
                      : 'Votre compte a été créé. Vous pouvez maintenant publier des annonces et produits.'}
              </p>
              <Link
                to="/dashboard"
                className="inline-block px-6 py-2.5 bg-primary-500 text-foreground-950 font-semibold rounded-lg hover:bg-primary-400 transition-colors text-sm"
              >
                Accéder à mon tableau de bord
              </Link>
            </div>
          ) : verificationPending ? (
            <form onSubmit={handleVerify} className="space-y-5">
              <div className="text-center">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-primary-500/20">
                  <i className="ri-shield-keyhole-line text-3xl text-primary-400" />
                </div>
                <h2 className="font-heading font-bold text-xl text-foreground-50 mb-2">
                  Vérifiez votre email
                </h2>
                <p className="text-sm text-foreground-400">
                  Un code de vérification à 6 chiffres a été envoyé à{' '}
                  <span className="text-foreground-50 font-medium">{formData.email}</span>.
                </p>
              </div>

              <div>
                <label htmlFor="code" className="block text-sm font-medium text-foreground-400 mb-1.5">
                  Code de vérification
                </label>
                <input
                  id="code"
                  name="code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  required
                  maxLength={6}
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/[^0-9]/g, ''))}
                  placeholder="••••••"
                  className="w-full bg-background-100 border border-background-200/70 rounded-lg py-3 px-4 text-center text-2xl tracking-[0.5em] text-foreground-50 placeholder:text-foreground-500 focus:border-primary-400/50 outline-none"
                />
              </div>

              {status === 'error' && (
                <div className="p-3 rounded-lg bg-red-500/10 border border-red-400/30 text-sm text-red-400">
                  {errorMsg}
                </div>
              )}

              <button
                type="submit"
                disabled={status === 'loading' || verificationCode.length !== 6}
                className="w-full py-3 bg-primary-500 text-foreground-950 font-semibold rounded-lg hover:bg-primary-400 transition-colors disabled:opacity-60 whitespace-nowrap"
              >
                {status === 'loading' ? 'Vérification...' : 'Vérifier le code'}
              </button>

              <div className="text-center space-y-2">
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={resending || resendCooldown > 0}
                  className="text-sm text-primary-400 hover:text-primary-300 transition-colors disabled:opacity-60"
                >
                  {resending
                    ? 'Envoi en cours...'
                    : resendCooldown > 0
                      ? `Renvoyer le code (${resendCooldown}s)`
                      : 'Renvoyer le code'}
                </button>
                <button
                  type="button"
                  onClick={() => setVerificationPending(false)}
                  className="block text-sm text-foreground-400 hover:text-foreground-400 transition-colors mx-auto"
                >
                  Modifier mes informations
                </button>
              </div>
            </form>
          ) : (
            <form
              onSubmit={
                step === totalSteps
                  ? handleSubmit
                  : (e) => {
                      e.preventDefault();
                      setStep(step + 1);
                    }
              }
              className="space-y-5"
            >
              {/* Step 1: Account type + credentials */}
              {step === 1 && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-foreground-400 mb-2">
                      Vous êtes ?
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => setAccountType('particulier')}
                        className={`p-4 rounded-lg border text-center transition-colors ${
                          accountType === 'particulier'
                            ? 'border-primary-400/60 bg-primary-500/10'
                            : 'border-background-300/60 bg-background-100 hover:border-primary-400/30'
                        }`}
                      >
                        <span className="block text-2xl mb-1 text-foreground-50">
                          <i className="ri-user-line" />
                        </span>
                        <span className="block text-sm font-medium text-foreground-50">Particulier</span>
                        <span className="block text-xs text-foreground-400 mt-1">
                          Publier des annonces &amp; produits
                        </span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setAccountType('entreprise')}
                        className={`p-4 rounded-lg border text-center transition-colors ${
                          accountType === 'entreprise'
                            ? 'border-primary-400/60 bg-primary-500/10'
                            : 'border-background-300/60 bg-background-100 hover:border-primary-400/30'
                        }`}
                      >
                        <span className="block text-2xl mb-1 text-foreground-50">
                          <i className="ri-store-2-line" />
                        </span>
                        <span className="block text-sm font-medium text-foreground-50">Entreprise</span>
                        <span className="block text-xs text-foreground-400 mt-1">
                          Annuaire + annonces pro
                        </span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setAccountType('freelance')}
                        className={`p-4 rounded-lg border text-center transition-colors ${
                          accountType === 'freelance'
                            ? 'border-primary-400/60 bg-primary-500/10'
                            : 'border-background-300/60 bg-background-100 hover:border-primary-400/30'
                        }`}
                      >
                        <span className="block text-2xl mb-1 text-foreground-50">
                          <i className="ri-user-star-line" />
                        </span>
                        <span className="block text-sm font-medium text-foreground-50">Freelance</span>
                        <span className="block text-xs text-foreground-400 mt-1">
                          Proposer des services
                        </span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setAccountType('formateur')}
                        className={`p-4 rounded-lg border text-center transition-colors ${
                          accountType === 'formateur'
                            ? 'border-primary-400/60 bg-primary-500/10'
                            : 'border-background-300/60 bg-background-100 hover:border-primary-400/30'
                        }`}
                      >
                        <span className="block text-2xl mb-1 text-foreground-50">
                          <i className="ri-graduation-cap-line" />
                        </span>
                        <span className="block text-sm font-medium text-foreground-50">Formateur</span>
                        <span className="block text-xs text-foreground-400 mt-1">
                          Publier des formations
                        </span>
                      </button>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="fullName" className="block text-sm font-medium text-foreground-400 mb-1.5">
                      {isBusiness ? 'Nom du responsable' : 'Nom complet'}
                    </label>
                    <div className="flex items-center bg-background-100 border border-background-200/70 rounded-lg overflow-hidden focus-within:border-primary-400/50">
                      <div className="w-10 h-10 flex items-center justify-center shrink-0">
                        <i className="ri-user-line text-foreground-400" />
                      </div>
                      <input
                        id="fullName"
                        name="fullName"
                        type="text"
                        required
                        value={formData.fullName}
                        onChange={(e) => updateField('fullName', e.target.value)}
                        placeholder="Jean Dupont"
                        className="flex-1 bg-transparent text-sm text-foreground-50 placeholder:text-foreground-500 py-2.5 pr-3 outline-none"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground-400 mb-1.5">
                      Adresse email
                    </label>
                    <div className="flex items-center bg-background-100 border border-background-200/70 rounded-lg overflow-hidden focus-within:border-primary-400/50">
                      <div className="w-10 h-10 flex items-center justify-center shrink-0">
                        <i className="ri-mail-line text-foreground-400" />
                      </div>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => updateField('email', e.target.value)}
                        placeholder="votre@email.com"
                        className="flex-1 bg-transparent text-sm text-foreground-50 placeholder:text-foreground-500 py-2.5 pr-3 outline-none"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-foreground-400 mb-1.5">
                      Téléphone
                    </label>
                    <div className="flex items-center bg-background-100 border border-background-200/70 rounded-lg overflow-hidden focus-within:border-primary-400/50">
                      <div className="w-10 h-10 flex items-center justify-center shrink-0">
                        <i className="ri-phone-line text-foreground-400" />
                      </div>
                      <input
                        id="phone"
                        name="phone"
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={(e) => updateField('phone', e.target.value)}
                        placeholder="+228 90 00 00 00"
                        className="flex-1 bg-transparent text-sm text-foreground-50 placeholder:text-foreground-500 py-2.5 pr-3 outline-none"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-foreground-400 mb-1.5">
                      Mot de passe
                    </label>
                    <div className="flex items-center bg-background-100 border border-background-200/70 rounded-lg overflow-hidden focus-within:border-primary-400/50">
                      <div className="w-10 h-10 flex items-center justify-center shrink-0">
                        <i className="ri-lock-line text-foreground-400" />
                      </div>
                      <input
                        id="password"
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        required
                        minLength={8}
                        value={formData.password}
                        onChange={(e) => updateField('password', e.target.value)}
                        placeholder="••••••••"
                        className="flex-1 bg-transparent text-sm text-foreground-50 placeholder:text-foreground-500 py-2.5 pr-3 outline-none"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="w-10 h-10 flex items-center justify-center shrink-0 text-foreground-400 hover:text-foreground-400"
                      >
                        <i className={showPassword ? 'ri-eye-line' : 'ri-eye-off-line'} />
                      </button>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-foreground-400 mb-1.5">
                      Confirmer le mot de passe
                    </label>
                    <div className="flex items-center bg-background-100 border border-background-200/70 rounded-lg overflow-hidden focus-within:border-primary-400/50">
                      <div className="w-10 h-10 flex items-center justify-center shrink-0">
                        <i className="ri-lock-line text-foreground-400" />
                      </div>
                      <input
                        id="confirmPassword"
                        name="confirmPassword"
                        type="password"
                        required
                        minLength={8}
                        value={formData.confirmPassword}
                        onChange={(e) => updateField('confirmPassword', e.target.value)}
                        placeholder="••••••••"
                        className="flex-1 bg-transparent text-sm text-foreground-50 placeholder:text-foreground-500 py-2.5 pr-3 outline-none"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="sector" className="block text-sm font-medium text-foreground-400 mb-1.5">
                      Votre secteur d&apos;activité (domaine)
                    </label>
                    <div className="flex items-center bg-background-100 border border-background-200/70 rounded-lg overflow-hidden focus-within:border-primary-400/50">
                      <div className="w-10 h-10 flex items-center justify-center shrink-0">
                        <i className="ri-briefcase-line text-foreground-400" />
                      </div>
                      <select
                        id="sector"
                        name="sector"
                        value={formData.sector}
                        onChange={(e) => updateField('sector', e.target.value)}
                        className="flex-1 bg-transparent text-sm text-foreground-50 py-2.5 pr-3 outline-none cursor-pointer"
                      >
                        <option value="">Sélectionnez votre secteur</option>
                        <optgroup label="Secteurs d'activité">
                          {businessSectors.map((s) => (
                            <option key={s.id} value={s.name}>{s.name}</option>
                          ))}
                        </optgroup>
                        <optgroup label="Catégories de métiers">
                          {professionCategories.map((cat) => (
                            <option key={cat.id} value={cat.name}>{cat.name}</option>
                          ))}
                        </optgroup>
                      </select>
                    </div>
                  </div>
                </>
              )}

              {/* Step 2: Business (only for entreprise) */}
              {step === 2 && isBusiness && (
                <>
                  <div>
                    <label htmlFor="businessName" className="block text-sm font-medium text-foreground-400 mb-1.5">
                      Nom de l&apos;entreprise
                    </label>
                    <div className="flex items-center bg-background-100 border border-background-200/70 rounded-lg overflow-hidden focus-within:border-primary-400/50">
                      <div className="w-10 h-10 flex items-center justify-center shrink-0">
                        <i className="ri-store-line text-foreground-400" />
                      </div>
                      <input
                        id="businessName"
                        name="businessName"
                        type="text"
                        required
                        value={formData.businessName}
                        onChange={(e) => updateField('businessName', e.target.value)}
                        placeholder="Mon Entreprise SARL"
                        className="flex-1 bg-transparent text-sm text-foreground-50 placeholder:text-foreground-500 py-2.5 pr-3 outline-none"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="category" className="block text-sm font-medium text-foreground-400 mb-1.5">
                      Catégorie
                    </label>
                    <div className="flex items-center bg-background-100 border border-background-200/70 rounded-lg overflow-hidden focus-within:border-primary-400/50">
                      <div className="w-10 h-10 flex items-center justify-center shrink-0">
                        <i className="ri-folder-line text-foreground-400" />
                      </div>
                      <select
                        id="category"
                        name="category"
                        required
                        value={formData.category}
                        onChange={(e) => updateField('category', e.target.value)}
                        className="flex-1 bg-transparent text-sm text-foreground-50 py-2.5 pr-3 outline-none cursor-pointer"
                      >
                        <option value="" disabled>Sélectionnez une catégorie</option>
                        {categories.map((cat) => (
                          <option key={cat.value} value={cat.value}>{cat.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="city" className="block text-sm font-medium text-foreground-400 mb-1.5">
                      Ville
                    </label>
                    <div className="flex items-center bg-background-100 border border-background-200/70 rounded-lg overflow-hidden focus-within:border-primary-400/50">
                      <div className="w-10 h-10 flex items-center justify-center shrink-0">
                        <i className="ri-map-pin-line text-foreground-400" />
                      </div>
                      <select
                        id="city"
                        name="city"
                        required
                        value={formData.city}
                        onChange={(e) => updateField('city', e.target.value)}
                        className="flex-1 bg-transparent text-sm text-foreground-50 py-2.5 pr-3 outline-none cursor-pointer"
                      >
                        <option value="" disabled>Sélectionnez une ville</option>
                        {cities.map((city) => (
                          <option key={city} value={city}>{city}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="address" className="block text-sm font-medium text-foreground-400 mb-1.5">
                      Adresse complète
                    </label>
                    <div className="flex items-start bg-background-100 border border-background-200/70 rounded-lg overflow-hidden focus-within:border-primary-400/50">
                      <div className="w-10 h-10 flex items-center justify-center shrink-0 mt-1">
                        <i className="ri-road-map-line text-foreground-400" />
                      </div>
                      <textarea
                        id="address"
                        name="address"
                        required
                        value={formData.address}
                        onChange={(e) => updateField('address', e.target.value)}
                        placeholder="123 Rue Principale, Quartier..."
                        maxLength={500}
                        rows={3}
                        className="flex-1 bg-transparent text-sm text-foreground-50 placeholder:text-foreground-500 py-2.5 pr-3 outline-none resize-none"
                      />
                    </div>
                  </div>
                </>
              )}

              {/* Step 2: Freelance (titre + compétences) */}
              {step === 2 && isFreelance && (
                <>
                  <div>
                    <label htmlFor="headline" className="block text-sm font-medium text-foreground-400 mb-1.5">
                      Titre professionnel
                    </label>
                    <div className="flex items-center bg-background-100 border border-background-200/70 rounded-lg overflow-hidden focus-within:border-primary-400/50">
                      <div className="w-10 h-10 flex items-center justify-center shrink-0">
                        <i className="ri-briefcase-line text-foreground-400" />
                      </div>
                      <select
                        id="headline"
                        name="headline"
                        required
                        value={formData.headline}
                        onChange={(e) => updateField('headline', e.target.value)}
                        className="flex-1 bg-transparent text-sm text-foreground-50 py-2.5 pr-3 outline-none cursor-pointer"
                      >
                        <option value="" disabled>Sélectionnez votre métier</option>
                        {headlineOptions.length > 0
                          ? headlineOptions.map((p) => (
                              <option key={p} value={p}>{p}</option>
                            ))
                          : professionCategories.map((cat) => (
                              <optgroup key={cat.id} label={cat.name}>
                                {cat.professions.map((p) => (
                                  <option key={p} value={p}>{p}</option>
                                ))}
                              </optgroup>
                            ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="skills" className="block text-sm font-medium text-foreground-400 mb-1.5">
                      Compétences
                    </label>
                    <div className="flex items-center bg-background-100 border border-background-200/70 rounded-lg overflow-hidden focus-within:border-primary-400/50">
                      <div className="w-10 h-10 flex items-center justify-center shrink-0">
                        <i className="ri-tools-line text-foreground-400" />
                      </div>
                      <input
                        id="skills"
                        name="skills"
                        type="text"
                        required
                        value={formData.skills}
                        onChange={(e) => updateField('skills', e.target.value)}
                        placeholder="Ex. React, TypeScript, SEO"
                        className="flex-1 bg-transparent text-sm text-foreground-50 placeholder:text-foreground-500 py-2.5 pr-3 outline-none"
                      />
                    </div>
                    <p className="text-xs text-foreground-500 mt-1">Séparez vos compétences par des virgules.</p>
                  </div>
                </>
              )}

              {/* Step 2: Formateur (domaine d'expertise) */}
              {step === 2 && isFormateur && (
                <div>
                  <label htmlFor="expertise" className="block text-sm font-medium text-foreground-400 mb-1.5">
                    Domaine d&apos;expertise
                  </label>
                  <div className="flex items-center bg-background-100 border border-background-200/70 rounded-lg overflow-hidden focus-within:border-primary-400/50">
                    <div className="w-10 h-10 flex items-center justify-center shrink-0">
                      <i className="ri-graduation-cap-line text-foreground-400" />
                    </div>
                    <select
                      id="expertise"
                      name="expertise"
                      required
                      value={formData.expertise}
                      onChange={(e) => updateField('expertise', e.target.value)}
                      className="flex-1 bg-transparent text-sm text-foreground-50 py-2.5 pr-3 outline-none cursor-pointer"
                    >
                      <option value="" disabled>Sélectionnez votre domaine</option>
                      {professionCategories.map((cat) => (
                        <option key={cat.id} value={cat.name}>{cat.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {/* Step 3 (or 2 for particulier): Terms */}
              {step === totalSteps && (
                <>
                  <div className="p-4 rounded-lg bg-background-100 border border-background-200/70">
                    <h3 className="font-heading font-semibold text-base text-foreground-50 mb-3">
                      Récapitulatif
                    </h3>
                    <div className="space-y-2 text-sm">
                      <p className="text-foreground-400"><span className="text-foreground-400">Type de compte :</span> {typeLabels[accountType]}</p>
                      <p className="text-foreground-400"><span className="text-foreground-400">Nom :</span> {formData.fullName}</p>
                      <p className="text-foreground-400"><span className="text-foreground-400">Email :</span> {formData.email}</p>
                      {formData.sector && (
                        <p className="text-foreground-400"><span className="text-foreground-400">Secteur :</span> {formData.sector}</p>
                      )}
                      {isBusiness && (
                        <>
                          <p className="text-foreground-400"><span className="text-foreground-400">Entreprise :</span> {formData.businessName}</p>
                          <p className="text-foreground-400"><span className="text-foreground-400">Catégorie :</span> {formData.category}</p>
                          <p className="text-foreground-400"><span className="text-foreground-400">Ville :</span> {formData.city}</p>
                        </>
                      )}
                      {isFreelance && (
                        <>
                          <p className="text-foreground-400"><span className="text-foreground-400">Titre pro :</span> {formData.headline}</p>
                          <p className="text-foreground-400"><span className="text-foreground-400">Compétences :</span> {formData.skills}</p>
                        </>
                      )}
                      {isFormateur && (
                        <p className="text-foreground-400"><span className="text-foreground-400">Expertise :</span> {formData.expertise}</p>
                      )}
                    </div>
                  </div>

                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      required
                      checked={formData.termsAccepted}
                      onChange={(e) => updateField('termsAccepted', e.target.checked)}
                      className="mt-0.5 accent-primary-500 w-4 h-4 shrink-0"
                    />
                    <span className="text-sm text-foreground-400">
                      J&apos;accepte les{' '}
                      <span className="text-primary-400 hover:text-primary-300 cursor-pointer">conditions d&apos;utilisation</span>
                      {' '}et la{' '}
                      <span className="text-primary-400 hover:text-primary-300 cursor-pointer">politique de confidentialité</span>.
                    </span>
                  </label>
                </>
              )}

              {/* Honeypot */}
              <div className="website_alt_field">
                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
              </div>

              {/* Error */}
              {status === 'error' && (
                <div className="p-3 rounded-lg bg-red-500/10 border border-red-400/30 text-sm text-red-400">
                  {errorMsg}
                </div>
              )}

              {/* Buttons */}
              <div className="flex gap-3">
                {step > 1 && (
                  <button
                    type="button"
                    onClick={() => setStep(step - 1)}
                    className="flex-1 py-3 border border-background-300/60 text-foreground-50 font-semibold rounded-lg hover:bg-foreground-50/5 transition-colors whitespace-nowrap"
                  >
                    Retour
                  </button>
                )}
                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="flex-1 py-3 bg-primary-500 text-foreground-950 font-semibold rounded-lg hover:bg-primary-400 transition-colors disabled:opacity-60 whitespace-nowrap"
                >
                  {step === totalSteps
                    ? (status === 'loading' ? 'Inscription en cours...' : 'Finaliser l\'inscription')
                    : 'Continuer'}
                </button>
              </div>
            </form>
          )}

          {/* Link to login */}
          {!verificationPending && status !== 'success' && (
            <p className="mt-6 text-center text-sm text-foreground-400">
              Déjà inscrit ?{' '}
              <Link to="/auth/login" className="text-primary-400 hover:text-primary-300 font-medium transition-colors">
                Se connecter
              </Link>
            </p>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default Register;